// Merges repeat-fired lines of the same product (fired in separate batches) into a single
// bill row with the summed quantity, so Prepare Bill / Final Bill show one line per product
// instead of one line per fire batch. KOT printing from cart.items is unaffected (separate
// data source in printController.js), but CWHardwareController.js's production-center/KOT
// grouping (lines 100-136) reads this same receiptData.items and keys off `item.order` to
// decide whether a line still needs to be sent to the kitchen — so `order` MUST be part of
// the merge key. Combining an already-fired line (order:"Y") with a not-yet-fired line
// (order:"N") for the same product would either duplicate the KOT or silently drop the
// unfired portion from ever reaching the kitchen.
//
// Which numeric fields are safe to sum was derived from the live formulas (cart.js:1373-1375,
// useOrderProcessing.js:640-641, useCartOperations.js:916-919, et al. — all consistent):
//   linegross = nettotal              (pure alias)
//   linetax   = taxAmount             (pure alias)
//   linenet   = nettotal - taxAmount  (pure difference)
//   linestd   = grossStd * weight     (grossStd is a UNIT-rate value from sunitprice/taxRate)
// The first three are exact linear combinations of nettotal/taxAmount, so summing them directly
// equals recomputing them, unconditionally. linestd is only safe to sum if grossStd is identical
// across the merged lines — enforced by including sunitprice/listPrice/taxFlag in the merge key.
// Unit-rate fields (netunit, listprice, grossunit, grossstd, grosslist, netList, unitPrice,
// unitTax, netStd, salePrice, realPrice) are correctly left untouched: the merge key already
// requires them to be identical across merged lines, so the first line's value stays valid.
const SUMMED_FIELDS = ["weight", "nettotal", "taxAmount", "discount", "linegross", "linetax", "linenet", "linestd", "firedQty", "sendQty"];

const addonsKey = (item) => {
  const addons = item?.selectedAddons?.length > 0 ? item.selectedAddons : item?.addons;
  if (!Array.isArray(addons) || addons.length === 0) return "";
  return addons
    .map((addon) => `${addon?.mProductAddOnId ?? addon?.productId ?? addon?.id ?? ""}:${addon?.qty ?? addon?.addonQty ?? addon?.quantity ?? 1}`)
    .sort()
    .join("|");
};

// Lines that must never be silently combined with another line, even if productId matches.
// Note: `parkedItem` is NOT a "held bill" flag here — useOrderHandlers.js stamps it `true` on
// every already-fired restaurant line (MenuLayout.js:772-775 reads `parkedItem === false` as
// "pending to send"), so it must NOT be used to exclude lines from merging.
const isMergeable = (item) =>
  item?.addNewLine !== "Y" &&
  !(item?.childItems?.length > 0) &&
  !(item?.children?.length > 0) &&
  item?.giftCardType !== "DIG" &&
  !item?.isGiftCard;

const mergeKey = (item) =>
  [
    item.productId || item.mProductId || "",
    item.upc || "",
    item.mBatchId || "",
    item.uom || item.csUomId || "",
    item.isReturn ? "R" : "S",
    item.order === "Y" ? "Y" : "N", // fire status — see comment above
    item.realPrice ?? item.salePrice ?? "",
    item.sunitprice ?? "",
    item.listPrice ?? "",
    item.taxRate ?? "",
    item.taxFlag || "",
    item.discountType || "",
    item.discountName || "",
    item.mPricingruleId || "",
    (item.notes || "").trim(),
    addonsKey(item),
  ].join("::");

export const consolidateBillItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) return items;

  const merged = [];
  const indexByKey = new Map();

  items.forEach((item) => {
    if (!isMergeable(item)) {
      merged.push(item);
      return;
    }

    const key = mergeKey(item);
    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, merged.length);
      merged.push({ ...item });
      return;
    }

    const target = merged[existingIndex];
    SUMMED_FIELDS.forEach((field) => {
      if (item[field] !== undefined && item[field] !== null) {
        target[field] = (Number(target[field]) || 0) + (Number(item[field]) || 0);
      }
    });
  });

  return merged;
};
